import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'

/**
 * The dialog the manager pages put their confirmations, details and forms in.
 *
 * A port of `Components/Shared/Modals/Modal.razor`, whose behaviour it keeps: Escape closes,
 * clicking the overlay closes unless told otherwise, a click inside does not, and focus moves to
 * the overlay when it opens so the keyboard starts inside the dialog rather than behind it.
 *
 * **Hand-rolled rather than built on `@radix-ui/react-dialog`** (no longer installed). Radix is used
 * in the shell, and the measurement recorded there is the reason it is not used here: against
 * Radix's focus guards and popper, `userEvent` costs ~9.5s for a trigger click and ~11s for a menu
 * item, versus ~2ms for `fireEvent` — two clicks exceeded the 5s timeout and presented as a hung
 * component. The three manager pages need about eleven dialogs between them and their tests are
 * click-heavy, so that cost would be paid over and over. `CredentialDialog` is already hand-rolled
 * for the same reason, and this is that idiom extracted rather than a fourth copy of it.
 *
 * The Blazor original also hand-rolled it, and `FileManager.razor` then ignored it and wrote its own
 * overlay markup inline — so the old portal had two dialog implementations that looked alike and
 * behaved differently. One is the point of this file.
 *
 * **Focus is kept in and given back.** Tab and Shift+Tab cycle through the dialog's own controls
 * rather than walking out into the page behind the overlay, focus that lands outside (a click on
 * the page's chrome, a script) is brought back, and on close focus returns to whatever opened the
 * dialog, so a keyboard user is not dropped at the top of the document. A dialog opened from inside
 * another traps on its own while it is open; the one beneath resumes when it closes.
 */

/** What Tab can reach. Disabled controls and `tabindex="-1"` are left out, as the browser does. */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',')

function focusablesIn(root: HTMLElement | null): HTMLElement[] {
  if (!root) return []
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true',
  )
}

/** Open dialogs, innermost last. Only the innermost one keeps focus. */
const openDialogs: HTMLElement[] = []

export interface DialogProps {
  /** Names the dialog for assistive technology, and titles it on screen. */
  title: string
  onClose: () => void
  /**
   * The Blazor `Modal` defaulted this to true and every page took the default. A form with typed-in
   * work is the case where a stray click on the backdrop should not discard it.
   */
  closeOnOverlayClick?: boolean
  size?: DialogSize
  children: ReactNode
  /** The action row. Kept a slot so each dialog names its own verb rather than sharing an "OK". */
  footer?: ReactNode
}

export type DialogSize = 'sm' | 'md' | 'lg' | 'wide' | 'xl'

/**
 * The widths from `base.css`, which are 400 / 500 / 600 / 1400px against a `width: 90%` cap, plus one
 * of our own at 900px.
 *
 * **`.modal-lg` is declared twice** — `max-width: 800px` at line 941 and `600px` at line 993 — so
 * the later one wins and every "large" modal has silently been 600px, not 800. The measured
 * behaviour is ported rather than the first declaration, because 600 is what operators have
 * actually been looking at; `xl` at 1400px is untouched and is what `ConfigTemplates/Edit` needs.
 */
const WIDTHS: Record<DialogSize, string> = {
  sm: 'w-[min(400px,90%)]',
  md: 'w-[min(500px,90%)]',
  lg: 'w-[min(600px,90%)]',
  // Between the two, and added rather than widening `lg`, which eight other dialogs rely on at the
  // 600px operators have always seen. This is for a dialog of *lines* — a log, a timeline — where
  // 600px wraps text that reads as one line per event and 1400px is a wall of empty space.
  wide: 'w-[min(900px,90%)]',
  xl: 'w-[min(1400px,90%)]',
}

export function Dialog({
  title,
  onClose,
  closeOnOverlayClick = true,
  size = 'md',
  children,
  footer,
}: DialogProps) {
  const overlay = useRef<HTMLDivElement | null>(null)
  const panel = useRef<HTMLDivElement | null>(null)
  // Read during the first render, before anything inside can take focus (an `autoFocus` field does
  // so during commit, ahead of any effect), so this is whatever had focus when the dialog opened.
  const [opener] = useState(() =>
    typeof document === 'undefined' ? null : (document.activeElement as HTMLElement | null),
  )

  useEffect(() => {
    const node = overlay.current
    if (!node) return
    openDialogs.push(node)

    // Into the dialog, unless a field inside already took focus for itself. Best-effort, as the
    // original was: a dialog that cannot take focus is still a usable dialog.
    if (!node.contains(document.activeElement)) node.focus()

    const onFocusIn = (event: FocusEvent) => {
      if (openDialogs[openDialogs.length - 1] !== node) return
      if (event.target instanceof Node && node.contains(event.target)) return
      ;(focusablesIn(panel.current)[0] ?? node).focus()
    }
    document.addEventListener('focusin', onFocusIn)

    return () => {
      document.removeEventListener('focusin', onFocusIn)
      const at = openDialogs.lastIndexOf(node)
      if (at !== -1) openDialogs.splice(at, 1)
      // Back to the opener, if it is still there to take it: a row deleted by this very dialog's
      // action is gone, and focusing a detached node does nothing useful.
      if (opener && opener !== document.body && opener.isConnected) opener.focus()
    }
  }, [opener])

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      // Stopped here so a dialog opened over another closes alone rather than taking both.
      event.stopPropagation()
      onClose()
      return
    }
    if (event.key !== 'Tab') return

    // Never empty: the header's Close button is always there.
    const items = focusablesIn(panel.current)
    const first = items[0]
    const last = items[items.length - 1]
    const active = document.activeElement
    const inside = panel.current?.contains(active) ?? false
    if (event.shiftKey && (active === first || !inside)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && (active === last || !inside)) {
      event.preventDefault()
      first.focus()
    }
  }

  return (
    <div
      ref={overlay}
      tabIndex={-1}
      onClick={() => {
        if (closeOnOverlayClick) onClose()
      }}
      onKeyDown={onKeyDown}
      className="fixed inset-0 z-modal flex items-center justify-center bg-overlay p-md outline-none"
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        // Without this a click on any control inside would reach the overlay and close the dialog.
        onClick={(event) => event.stopPropagation()}
        className={`${WIDTHS[size]} max-h-[90vh] overflow-auto rounded-lg border border-line bg-bg-secondary shadow-lg`}
      >
        <div className="flex items-center justify-between border-b border-line px-lg py-md">
          <h2 className="text-lg font-semibold text-fg">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md px-sm text-fg-secondary hover:bg-bg-hover hover:text-fg"
          >
            ×
          </button>
        </div>

        <div className="px-lg py-md text-sm text-fg">{children}</div>

        {footer ? (
          <div className="flex justify-end gap-sm border-t border-line px-lg py-md">{footer}</div>
        ) : null}
      </div>
    </div>
  )
}
